import './portfolio-immobiliare-filters.scss';

/**
 * version    1.0
 * class      PortfolioImmobiliareFilters
 *
 * Gestisce i filtri del portfolio immobiliare: luogo (autocomplete),
 * destinazione e patrimonio (dropdown con checkbox), superficie (range slider).
 * Desktop e mobile condividono lo stato: le selezioni vengono sincronizzate
 * tra i due contesti e riflesse nelle chip dei filtri attivi.
 */
export class PortfolioImmobiliareFilters {
  constructor(element) {
    this.element = element || document.querySelector('[data-component="filters-portfolio-immobiliare"]');
    if (this.element) {
      this.init();
    }
  }

  init() {
    // ── Riferimenti DOM ──────────────────────────────────────────────────────

    // Checkbox desktop (nei dropdown) + checkbox mobile (nei modal Destinazione e
    // Patrimonio): vengono unite in this.allCheckboxes per sincronizzare i contesti
    this.desktopCheckboxes = this.element.querySelectorAll('.filters-portfolio-immobiliare__dropdown-list .form-check-input');

    // Trigger dei dropdown Bootstrap desktop (bottoni che mostrano la selezione corrente)
    this.destinazioneTrigger = this.element.querySelector('#destinazioneDropdownTrigger');
    this.patrimonioTrigger = this.element.querySelector('#patrimonioDropdownTrigger');
    this.superficieTrigger = this.element.querySelector('#superficieDropdownTrigger');

    // Trigger select mobile (nel pannello accordion)
    this.destinazioneMobileTrigger = document.getElementById('destinazioneMobileTrigger');
    this.patrimonioMobileTrigger = document.getElementById('patrimonioMobileTrigger');
    this.superficieMobileTrigger = document.getElementById('superficieMobileTrigger');
    this.superficieMobileTriggerLabel = document.getElementById('superficieMobileTriggerLabel');

    // Accordion mobile: toggle button + pannello + badge conteggio filtri
    this.mobileFiltersToggle = document.getElementById('mobileFiltersToggle');
    this.mobileFiltersPanel = document.getElementById('mobileFiltersPanel');
    this.mobileFiltersBadge = document.getElementById('mobileFiltersCount');

    // Contenitori dove vengono iniettate dinamicamente le chip dei filtri attivi
    this.chipsContainer = this.element.querySelector('#activeFiltersContainer');
    this.mobileChipsContainer = this.element.querySelector('#activeFiltersContainerMobile');

    // Campo luogo: input desktop + input mobile (nel pannello) + lista autocomplete
    this.luogoInput = this.element.querySelector('#filter-luogo');
    this.luogoInputMobile = document.getElementById('filter-luogo-mobile');
    this.luogoAutocomplete = this.element.querySelector('#filter-luogo-listbox');
    this.luogoClearBtn = this.element.querySelector('.filters-portfolio-immobiliare__input-clear');
    this.luogoValue = null; // valore committed (null = nessuna selezione)

    // Input numerici superficie (desktop e mobile)
    this.superficieMinInput = this.element.querySelector('#filter-superficie-min');
    this.superficieMaxInput = this.element.querySelector('#filter-superficie-max');
    this.superficieMinInputMobile = document.getElementById('filter-superficie-min-m');
    this.superficieMaxInputMobile = document.getElementById('filter-superficie-max-m');
    this.superficieSliders = this.element.querySelectorAll('.filters-portfolio-immobiliare__slider');
    this.superficieSelected = false;

    // Bottoni mobile panel
    this.mobileRemoveAllBtn = document.getElementById('mobileRemoveAllBtn');

    // Raccogli checkbox mobile dai dropdown inline nel pannello accordion
    const mobileCheckboxes = this.mobileFiltersPanel
      ? Array.from(this.mobileFiltersPanel.querySelectorAll('.form-check-input'))
      : [];
    this.allCheckboxes = [...this.desktopCheckboxes, ...mobileCheckboxes];

    this.bindEvents();
    this.initSliders();
    this.updateState();
    this.preventLuogoLabelActive();
    this.preventUncheckedLabelActive();
  }

  // ── Slider superficie ──────────────────────────────────────────────────────

  // Dual-range slider: due <input type="range"> sovrapposti.
  // Impedisce l'incrocio dei thumb e aggiorna il fill colorato in tempo reale.
  initSliders() {
    this.superficieSliders.forEach((sliderEl) => {
      const thumbMin = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-thumb--min');
      const thumbMax = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-thumb--max');
      const fill = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-fill');

      if (!thumbMin || !thumbMax || !fill) return;

      // Calcola left/right% del fill in base ai valori correnti dei due thumb
      const updateFill = () => {
        const min = parseInt(thumbMin.min, 10);
        const max = parseInt(thumbMin.max, 10);
        const valMin = parseInt(thumbMin.value, 10);
        const valMax = parseInt(thumbMax.value, 10);
        const pctMin = ((valMin - min) / (max - min)) * 100;
        const pctMax = ((valMax - min) / (max - min)) * 100;
        fill.style.left = `${pctMin}%`;
        fill.style.right = `${100 - pctMax}%`;
      };

      thumbMin.addEventListener('input', () => {
        // Il thumb min non può superare il thumb max
        if (parseInt(thumbMin.value, 10) > parseInt(thumbMax.value, 10)) {
          thumbMin.value = thumbMax.value;
        }
        this.superficieSelected = true;
        this.syncSliderToInputs(sliderEl);
        updateFill();
      });

      thumbMax.addEventListener('input', () => {
        if (parseInt(thumbMax.value, 10) < parseInt(thumbMin.value, 10)) {
          thumbMax.value = thumbMin.value;
        }
        this.superficieSelected = true;
        this.syncSliderToInputs(sliderEl);
        updateFill();
      });

      updateFill();
    });
  }

  // Aggiorna gli input numerici in base alla posizione dei thumb.
  // Gli input desktop sono la source of truth per label e chip: se lo slider
  // è nel pannello mobile, sincronizziamo anche quelli desktop.
  syncSliderToInputs(sliderEl) {
    const thumbMin = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-thumb--min');
    const thumbMax = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-thumb--max');
    const isMobilePanel = sliderEl.closest('#mobileFiltersPanel');
    const minInput = isMobilePanel ? this.superficieMinInputMobile : this.superficieMinInput;
    const maxInput = isMobilePanel ? this.superficieMaxInputMobile : this.superficieMaxInput;

    if (minInput) minInput.value = thumbMin.value; // eslint-disable-line no-param-reassign
    if (maxInput) maxInput.value = thumbMax.value; // eslint-disable-line no-param-reassign

    // Porta i valori anche sugli input desktop (source of truth)
    if (isMobilePanel) {
      if (this.superficieMinInput) this.superficieMinInput.value = thumbMin.value;
      if (this.superficieMaxInput) this.superficieMaxInput.value = thumbMax.value;
    }

    this.updateSuperficieTriggerLabel();
    this.updateOutput();
  }

  // Aggiorna i thumb in base ai valori digitati negli input numerici (con clamp)
  syncInputsToSlider(sliderEl) {
    const thumbMin = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-thumb--min');
    const thumbMax = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-thumb--max');
    const fill = sliderEl.querySelector('.filters-portfolio-immobiliare__slider-fill');
    const isModal = sliderEl.closest('#mobileFiltersPanel');
    const minInput = isModal ? this.superficieMinInputMobile : this.superficieMinInput;
    const maxInput = isModal ? this.superficieMaxInputMobile : this.superficieMaxInput;

    if (!minInput || !maxInput || !thumbMin || !thumbMax) return;

    const rangeMin = parseInt(thumbMin.min, 10);
    const rangeMax = parseInt(thumbMin.max, 10);
    let valMin = parseInt(minInput.value, 10);
    let valMax = parseInt(maxInput.value, 10);

    // Clamp: assicura che i valori stiano nel range e non si incrocino
    valMin = Math.max(rangeMin, Math.min(valMin, valMax));
    valMax = Math.min(rangeMax, Math.max(valMax, valMin));

    thumbMin.value = valMin;
    thumbMax.value = valMax;

    if (fill) {
      const pctMin = ((valMin - rangeMin) / (rangeMax - rangeMin)) * 100;
      const pctMax = ((valMax - rangeMin) / (rangeMax - rangeMin)) * 100;
      fill.style.left = `${pctMin}%`;
      fill.style.right = `${100 - pctMax}%`;
    }
  }

  updateSuperficieTriggerLabel() {
    const valMin = this.superficieMinInput ? this.superficieMinInput.value : '0';
    const valMax = this.superficieMaxInput ? this.superficieMaxInput.value : '2300';
    const label = this.superficieSelected
      ? `Da ${valMin} a ${valMax} mq`
      : 'Seleziona superficie';
    const hasSelection = this.superficieSelected;

    // Aggiorna trigger desktop
    if (this.superficieTrigger) {
      const span = this.superficieTrigger.querySelector('span');
      if (span) span.textContent = label;
      this.superficieTrigger.classList.toggle('has-selection', hasSelection);
    }

    // Aggiorna trigger mobile (select nel pannello accordion)
    if (this.superficieMobileTriggerLabel) {
      this.superficieMobileTriggerLabel.textContent = label;
    }
    if (this.superficieMobileTrigger) {
      this.superficieMobileTrigger.classList.toggle('has-selection', hasSelection);
    }
  }

  getSuperficieChip() {
    if (!this.superficieSelected) return null;
    if (!this.superficieMinInput || !this.superficieMaxInput) return null;
    const valMin = this.superficieMinInput.value;
    const valMax = this.superficieMaxInput.value;
    return { label: `Da ${valMin} a ${valMax} mq`, value: 'superficie' };
  }

  // ── Workaround Bootstrap Italia ────────────────────────────────────────────

  // BI aggiunge automaticamente la classe 'active' al label quando l'input è
  // focalizzato, spostandolo sopra il campo. Qui viene rimossa con MutationObserver
  // perché il label deve restare statico (posizionato via CSS, non via JS di BI).
  preventLuogoLabelActive() {
    if (!this.luogoInput) return;
    const label = this.element.querySelector(`label[for="${this.luogoInput.id}"]`);
    if (!label) return;

    new MutationObserver(() => {
      if (label.classList.contains('active')) {
        label.classList.remove('active');
      }
    }).observe(label, { attributes: true, attributeFilter: ['class'] });
  }

  // Stesso workaround per i label delle checkbox: BI aggiunge 'active' al label
  // anche quando la checkbox è deselezionata; qui viene rimossa in quel caso.
  preventUncheckedLabelActive() {
    this.allCheckboxes.forEach((cb) => {
      const wrapper = cb.closest('.form-check');
      const label = wrapper && wrapper.querySelector('.form-check-label');
      if (!label) return;

      new MutationObserver(() => {
        if (label.classList.contains('active') && !cb.checked) {
          label.classList.remove('active');
        }
      }).observe(label, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // ── Autocomplete luogo ─────────────────────────────────────────────────────

  initLuogoAutocomplete() {
    if (!this.luogoInput || !this.luogoAutocomplete) return;

    // Inizializza autocomplete per l'input desktop
    this.initSingleAutocomplete(this.luogoInput, this.luogoAutocomplete, this.luogoClearBtn);

    // Per il mobile: clona la listbox del desktop, inseriscila nel wrapper mobile
    // e inizializza autocomplete anche su quell'input
    if (this.luogoInputMobile) {
      const mobileListbox = this.luogoAutocomplete.cloneNode(true);
      mobileListbox.id = 'filter-luogo-listbox-mobile';
      mobileListbox.setAttribute('aria-label', 'Suggerimenti luogo mobile');
      this.luogoInputMobile.setAttribute('aria-autocomplete', 'list');
      this.luogoInputMobile.setAttribute('aria-haspopup', 'listbox');
      this.luogoInputMobile.setAttribute('aria-expanded', 'false');
      this.luogoInputMobile.setAttribute('aria-controls', 'filter-luogo-listbox-mobile');
      const mobileWrapper = this.luogoInputMobile.closest('.filters-portfolio-immobiliare__input-wrapper');
      if (mobileWrapper) mobileWrapper.appendChild(mobileListbox);
      this.initSingleAutocomplete(this.luogoInputMobile, mobileListbox, null);
    }
  }

  // Autocomplete generico: funziona per qualsiasi coppia input + listbox
  initSingleAutocomplete(input, listbox, clearBtn) {
    const items = Array.from(listbox.querySelectorAll('li[role="option"]'));

    // Helper per mostrare/nascondere il bottone "×" (solo per il desktop che ce l'ha)
    const setClearBtn = (visible) => {
      if (!clearBtn) return;
      clearBtn.classList.toggle('d-none', !visible);
    };

    const closeList = () => {
      listbox.hidden = true; // eslint-disable-line no-param-reassign
      input.setAttribute('aria-expanded', 'false');
      setClearBtn(!!input.value.trim());
    };

    // Mostra tutti gli <li> e apre la tendina senza filtrare
    const openAllItems = () => {
      items.forEach((item) => {
        item.hidden = false; // eslint-disable-line no-param-reassign
      });
      listbox.hidden = false; // eslint-disable-line no-param-reassign
      input.setAttribute('aria-expanded', 'true');
    };

    // Filtra gli <li> in base alla query e apre la tendina se ci sono risultati
    const filterAndOpenList = (query) => {
      let hasVisible = false;
      items.forEach((item) => {
        const listItem = item;
        const text = (listItem.getAttribute('data-value') || listItem.textContent).toLowerCase();
        const matches = text.includes(query);
        listItem.hidden = !matches;
        if (matches) hasVisible = true;
      });
      listbox.hidden = !hasVisible; // eslint-disable-line no-param-reassign
      input.setAttribute('aria-expanded', hasVisible ? 'true' : 'false');
      setClearBtn(hasVisible);
    };

    // Committa la selezione: scrive il valore nell'input, sincronizza l'altro input
    const selectItem = (item) => {
      const value = item.getAttribute('data-value');
      input.value = value; // eslint-disable-line no-param-reassign
      this.luogoValue = { value, label: value };
      // Sincronizza il valore sull'altro input (desktop ↔ mobile)
      const otherInput = input === this.luogoInput ? this.luogoInputMobile : this.luogoInput;
      if (otherInput) otherInput.value = value;
      // Mostra la X sull'input desktop se era quello a non avere il clearBtn
      if (input !== this.luogoInput && this.luogoClearBtn) {
        this.luogoClearBtn.classList.remove('d-none');
      }
      closeList();
      setClearBtn(true);
      this.updateOutput();
    };

    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      if (!query) {
        setClearBtn(false);
        openAllItems();
        return;
      }
      filterAndOpenList(query);
    });

    // Apre la tendina al focus/click/tap sull'input, mostrando tutti i suggerimenti
    const handleOpen = () => {
      const query = input.value.trim().toLowerCase();
      if (query) {
        filterAndOpenList(query);
      } else {
        openAllItems();
      }
    };

    input.addEventListener('focus', handleOpen);
    // Listener 'click' separato per garantire l'apertura anche su mobile (touch)
    input.addEventListener('click', handleOpen);

    listbox.addEventListener('click', (e) => {
      const item = e.target.closest('li[role="option"]');
      if (item) selectItem(item);
    });

    // Navigazione da tastiera sull'input: ↓↑ per spostarsi tra le voci, Esc per chiudere
    input.addEventListener('keydown', (e) => {
      if (listbox.hidden) return;
      const visible = items.filter((i) => !i.hidden);
      const focused = listbox.querySelector('li:focus');
      const idx = focused ? visible.indexOf(focused) : -1;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = visible[idx + 1] || visible[0];
        if (next) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = visible[idx - 1] || visible[visible.length - 1];
        if (prev) prev.focus();
      } else if (e.key === 'Escape') {
        closeList();
        input.focus();
      }
    });

    // Navigazione da tastiera sugli <li>: ↑ sul primo riporta il focus all'input
    listbox.addEventListener('keydown', (e) => {
      const visible = items.filter((i) => !i.hidden);
      const focused = listbox.querySelector('li:focus');
      const idx = focused ? visible.indexOf(focused) : -1;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = visible[idx + 1];
        if (next) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx === 0) {
          input.focus();
        } else {
          const prev = visible[idx - 1];
          if (prev) prev.focus();
        }
      } else if (e.key === 'Enter' && focused) {
        e.preventDefault();
        selectItem(focused);
        input.focus();
      } else if (e.key === 'Escape') {
        closeList();
        input.focus();
      }
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = ''; // eslint-disable-line no-param-reassign
        this.luogoValue = null;
        setClearBtn(false);
        closeList();
        this.updateOutput();
        input.focus();
      });
    }

    // Chiude la tendina cliccando fuori dall'input-wrapper
    document.addEventListener('click', (e) => {
      const wrapper = input.closest('.filters-portfolio-immobiliare__input-wrapper');
      if (wrapper && !wrapper.contains(e.target)) {
        closeList();
      }
    });
  }

  // ── Binding eventi ─────────────────────────────────────────────────────────

  bindEvents() {
    // Checkbox Destinazione / Patrimonio: sincronizza desktop ↔ mobile e aggiorna chip
    this.allCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.syncCheckboxesByValue(checkbox.value, checkbox.checked);
        this.updateDropdownTriggers();
        this.updateOutput();
      });
    });

    this.initLuogoAutocomplete();

    // Accordion mobile: toggle visibilità pannello + rotazione chevron
    if (this.mobileFiltersToggle && this.mobileFiltersPanel) {
      this.mobileFiltersToggle.addEventListener('click', () => {
        const isOpen = this.mobileFiltersToggle.getAttribute('aria-expanded') === 'true';
        this.mobileFiltersToggle.setAttribute('aria-expanded', String(!isOpen));
        this.mobileFiltersPanel.hidden = isOpen;
      });
    }

    // Input numerici superficie desktop: aggiornano lo slider e il trigger label
    [this.superficieMinInput, this.superficieMaxInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('change', () => {
        this.superficieSelected = true;
        this.superficieSliders.forEach((sliderEl) => {
          if (!sliderEl.closest('#mobileFiltersPanel')) {
            this.syncInputsToSlider(sliderEl);
          }
        });
        this.updateSuperficieTriggerLabel();
        this.updateOutput();
      });
    });

    // Input numerici superficie mobile: aggiornano lo slider nel pannello mobile
    // e sincronizzano i valori sugli input desktop (source of truth)
    [this.superficieMinInputMobile, this.superficieMaxInputMobile].forEach((input) => {
      if (!input) return;
      input.addEventListener('change', () => {
        this.superficieSelected = true;
        if (this.superficieMinInput) this.superficieMinInput.value = this.superficieMinInputMobile.value;
        if (this.superficieMaxInput) this.superficieMaxInput.value = this.superficieMaxInputMobile.value;
        this.superficieSliders.forEach((sliderEl) => {
          if (sliderEl.closest('#mobileFiltersPanel')) {
            this.syncInputsToSlider(sliderEl);
          }
        });
        this.updateSuperficieTriggerLabel();
        this.updateOutput();
      });
    });

    if (this.mobileRemoveAllBtn) {
      this.mobileRemoveAllBtn.addEventListener('click', () => this.clearAll());
    }

    const removeAllBtns = this.element.querySelectorAll('.filters-portfolio-immobiliare__remove-all');
    removeAllBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearAll();
      });
    });

    // Click sulle chip (delegate): identifica il filtro dal data-value e lo rimuove
    const handleChipClick = (e) => {
      const btn = e.target.closest('button.chip');
      if (!btn) return;
      e.preventDefault();
      const value = btn.getAttribute('data-value');
      if (value === 'luogo') {
        this.clearLuogo();
      } else if (value === 'superficie') {
        this.resetSuperficie();
      } else {
        this.deselectCheckbox(value);
      }
    };

    if (this.chipsContainer) this.chipsContainer.addEventListener('click', handleChipClick);
    if (this.mobileChipsContainer) this.mobileChipsContainer.addEventListener('click', handleChipClick);
  }

  // ── Gestione checkbox ──────────────────────────────────────────────────────

  // Sincronizza tutte le checkbox con lo stesso value (desktop + mobile condividono
  // gli stessi valori, quindi un cambio in un contesto si riflette nell'altro)
  syncCheckboxesByValue(value, checked) {
    this.allCheckboxes.forEach((cb) => {
      if (cb.value === value) {
        cb.checked = checked; // eslint-disable-line no-param-reassign
        const wrapper = cb.closest('.form-check');
        if (wrapper) {
          wrapper.classList.toggle('is-selected', checked);
          const label = wrapper.querySelector('.form-check-label');
          if (!checked && label) label.classList.remove('active');
        }
      }
    });
  }

  // Aggiorna il testo dei trigger desktop e mobile per Destinazione e Patrimonio
  updateDropdownTriggers() {
    [
      {
        desktopTrigger: this.destinazioneTrigger,
        mobileTrigger: this.destinazioneMobileTrigger,
        filter: 'destinazione',
        defaultLabel: 'Seleziona destinazione',
      },
      {
        desktopTrigger: this.patrimonioTrigger,
        mobileTrigger: this.patrimonioMobileTrigger,
        filter: 'patrimonio',
        defaultLabel: 'Seleziona patrimonio',
      },
    ].forEach(({
      desktopTrigger, mobileTrigger, filter, defaultLabel,
    }) => {
      const selected = this.getSelectedByFilter(filter);
      const label = selected.length === 0
        ? defaultLabel
        : `${selected.length} Selezionat${selected.length === 1 ? 'o' : 'i'}`;

      // Aggiorna trigger desktop
      if (desktopTrigger) {
        const span = desktopTrigger.querySelector('span');
        if (span) span.textContent = label;
        desktopTrigger.classList.toggle('has-selection', selected.length > 0);
      }

      // Aggiorna trigger mobile (select nel pannello accordion)
      if (mobileTrigger) {
        const span = mobileTrigger.querySelector('span');
        if (span) span.textContent = label;
        mobileTrigger.classList.toggle('has-selection', selected.length > 0);
      }
    });
  }

  // Restituisce le checkbox selezionate appartenenti a un determinato filtro.
  // Il filterMap definisce quali values appartengono a destinazione vs patrimonio.
  getSelectedByFilter(filterName) {
    const filterMap = {
      destinazione: ['appartamento', 'autorimessa', 'box-auto', 'cantina', 'capannone', 'laboratorio', 'locale-commerciale', 'magazzino', 'negozio', 'posto-auto', 'rustico', 'terreno', 'ufficio', 'villa'],
      patrimonio: ['fintecna', 'ps-ex-iged', 'ps-ex-efim', 'cdp-immobiliare', 'pentagramma-piemonte', 'pentagramma-romagna', 'encc', 'ccmcp'],
    };
    const values = filterMap[filterName] || [];
    const seen = new Set();
    return this.allCheckboxes.filter((cb) => {
      if (cb.checked && values.includes(cb.value) && !seen.has(cb.value)) {
        seen.add(cb.value);
        return true;
      }
      return false;
    });
  }

  deselectCheckbox(value) {
    this.syncCheckboxesByValue(value, false);
    this.updateDropdownTriggers();
    this.updateOutput();
  }

  // ── Reset filtri ───────────────────────────────────────────────────────────

  clearLuogo() {
    this.luogoValue = null;
    if (this.luogoInput) this.luogoInput.value = '';
    if (this.luogoInputMobile) this.luogoInputMobile.value = '';
    if (this.luogoClearBtn) this.luogoClearBtn.classList.add('d-none');
    this.updateOutput();
  }

  resetSuperficie() {
    this.superficieSelected = false;
    const defaultMin = '0';
    const defaultMax = '5000';
    if (this.superficieMinInput) this.superficieMinInput.value = defaultMin;
    if (this.superficieMaxInput) this.superficieMaxInput.value = defaultMax;
    if (this.superficieMinInputMobile) this.superficieMinInputMobile.value = defaultMin;
    if (this.superficieMaxInputMobile) this.superficieMaxInputMobile.value = defaultMax;
    this.superficieSliders.forEach((sliderEl) => this.syncInputsToSlider(sliderEl));
    this.updateSuperficieTriggerLabel();
    this.updateOutput();
  }

  clearAll() {
    this.allCheckboxes.forEach((cb) => {
      cb.checked = false; // eslint-disable-line no-param-reassign
      const wrapper = cb.closest('.form-check');
      if (wrapper) {
        wrapper.classList.remove('is-selected');
        const label = wrapper.querySelector('.form-check-label');
        if (label) label.classList.remove('active');
      }
    });
    this.clearLuogo();
    this.resetSuperficie();
    this.updateDropdownTriggers();
    this.updateOutput();
  }

  // Allinea il DOM allo stato corrente (usato all'inizializzazione)
  updateState() {
    this.allCheckboxes.forEach((cb) => {
      const wrapper = cb.closest('.form-check');
      if (wrapper) wrapper.classList.toggle('is-selected', cb.checked);
    });
    this.updateDropdownTriggers();
    this.updateSuperficieTriggerLabel();
    this.updateOutput();
  }

  // ── Chip filtri attivi ─────────────────────────────────────────────────────

  // Raccoglie tutti i filtri attivi (luogo, checkbox, superficie) e li passa
  // a renderChips per aggiornare i contenitori desktop e mobile
  updateOutput() {
    const chips = [];

    if (this.luogoInput && this.luogoInput.value.trim()) {
      chips.push({ label: this.luogoInput.value.trim(), value: 'luogo' });
    }

    const seen = new Set();
    this.allCheckboxes.forEach((cb) => {
      if (cb.checked && !seen.has(cb.value)) {
        seen.add(cb.value);
        const label = cb.getAttribute('data-label') || cb.value;
        chips.push({ label, value: cb.value });
      }
    });

    const superficieChip = this.getSuperficieChip();
    if (superficieChip) chips.push(superficieChip);

    this.renderChips(chips);
    this.updateMobileFiltersBadge(chips.length);
  }

  // Aggiorna il badge "(N)" nel toggle mobile: visibile solo se ci sono filtri attivi
  updateMobileFiltersBadge(count) {
    if (!this.mobileFiltersBadge) return;
    if (count > 0) {
      this.mobileFiltersBadge.textContent = `(${count})`;
      this.mobileFiltersBadge.classList.remove('d-none');
    } else {
      this.mobileFiltersBadge.classList.add('d-none');
    }
  }

  // Genera il markup delle chip e lo inietta nei contenitori.
  // Se non ci sono chip, svuota i contenitori senza aggiungere il "Cancella tutti".
  renderChips(chips) {
    const containers = [];
    if (this.chipsContainer) containers.push({ el: this.chipsContainer, isMobile: false });
    if (this.mobileChipsContainer) containers.push({ el: this.mobileChipsContainer, isMobile: true });

    containers.forEach(({ el, isMobile }) => {
      const container = el;
      container.innerHTML = '';

      // Su mobile il bottone "Cancella tutti" è statico nell'HTML: va mostrato/nascosto.
      // Va gestito prima dell'early return, altrimenti quando chips.length === 0
      // il return impedisce di nascondere il bottone.
      if (isMobile && this.mobileRemoveAllBtn) {
        this.mobileRemoveAllBtn.classList.toggle('d-none', chips.length === 0);
      }

      if (chips.length === 0) return;

      chips.forEach(({ label, value }) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = isMobile ? 'chip flex-shrink-0' : 'chip';
        chip.dataset.value = value;
        chip.setAttribute('aria-label', `Rimuovi filtro ${label}`);
        chip.innerHTML = `
          <span>${label}</span>
          <svg aria-hidden="true" class="svg-icon svg-icon--remove-filter"><use xlink:href="#sprite-remove-filter"></use></svg>
        `;
        container.appendChild(chip);
      });

      // "Cancella tutti i filtri" appare solo nel banner desktop (generato dinamicamente)
      if (!isMobile) {
        const removeAllBtn = document.createElement('button');
        removeAllBtn.type = 'button';
        removeAllBtn.className = 'filters-portfolio-immobiliare__remove-all';
        removeAllBtn.textContent = 'Cancella tutti i filtri';
        removeAllBtn.addEventListener('click', () => this.clearAll());
        container.appendChild(removeAllBtn);
      }
    });
  }
}
